import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Phase 9: PlatformFee (carrier-wide) + PlatformServiceFee (per-service)
 * admin client. Used by the unified pricing workspace's MarkupConfig tile.
 */

export interface PlatformFee {
  id: string;
  carrierId: string;
  percent: number;
  flatCents: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformServiceFee {
  id: string;
  carrierServiceId: string;
  percent: number;
  flatCents: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarkupInput {
  percent: number;
  flatCents: number;
  active?: boolean;
}

/**
 * Thrown when DELETE on /carrier-markups returns 409 with affected services.
 * UI catches it, surfaces an AlertDialog, then re-issues with confirm=true.
 */
export class CarrierMarkupDeleteRequiresConfirmError extends Error {
  constructor(
    public affectedServiceCount: number,
    public affectedServices: { id: string; code: string; name: string }[]
  ) {
    super(
      `Carrier-wide markup delete requires confirmation — ${affectedServiceCount} services depend on it`
    );
    this.name = "CarrierMarkupDeleteRequiresConfirmError";
  }
}

// ============================================================================
// PlatformFee (carrier-wide default)
// ============================================================================

export function useGetCarrierMarkup(carrierId: string | null | undefined) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["carrier-markup", carrierId],
    enabled: !!token && !!carrierId,
    queryFn: async () => {
      const { data } = await axios.get<{ data: PlatformFee | null }>(
        `${API_URL}/api/v2/admin/platform-fees/${carrierId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
  });
}

export function useUpsertCarrierMarkup(options?: {
  onSuccess?: (data: PlatformFee) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      input,
    }: {
      carrierId: string;
      input: MarkupInput;
    }): Promise<PlatformFee> => {
      const { data } = await axios.put<{ data: PlatformFee }>(
        `${API_URL}/api/v2/admin/platform-fees/${carrierId}`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-markup"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => options?.onError?.(err),
  });
}

export function useDeleteCarrierMarkup(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      confirm,
    }: {
      carrierId: string;
      confirm?: boolean;
    }): Promise<void> => {
      try {
        await axios.delete(
          `${API_URL}/api/v2/admin/platform-fees/${carrierId}${
            confirm ? "?confirm=true" : ""
          }`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          const data = err.response.data as
            | {
                error?: string;
                details?: {
                  affectedServiceCount: number;
                  affectedServices: {
                    id: string;
                    code: string;
                    name: string;
                  }[];
                };
              }
            | undefined;
          if (data?.error === "DELETE_REQUIRES_CONFIRM" && data.details) {
            throw new CarrierMarkupDeleteRequiresConfirmError(
              data.details.affectedServiceCount,
              data.details.affectedServices
            );
          }
        }
        throw err;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-markup"] });
      options?.onSuccess?.();
    },
    onError: (err) => options?.onError?.(err),
  });
}

// ============================================================================
// PlatformServiceFee (per-service override)
// ============================================================================

export function useGetCarrierServiceMarkup(
  carrierServiceId: string | null | undefined
) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["carrier-service-markup", carrierServiceId],
    enabled: !!token && !!carrierServiceId,
    queryFn: async () => {
      const { data } = await axios.get<{ data: PlatformServiceFee | null }>(
        `${API_URL}/api/v2/admin/platform-service-fees/${carrierServiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
  });
}

export function useUpsertCarrierServiceMarkup(options?: {
  onSuccess?: (data: PlatformServiceFee) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierServiceId,
      input,
    }: {
      carrierServiceId: string;
      input: MarkupInput;
    }): Promise<PlatformServiceFee> => {
      const { data } = await axios.put<{ data: PlatformServiceFee }>(
        `${API_URL}/api/v2/admin/platform-service-fees/${carrierServiceId}`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-service-markup"],
      });
      options?.onSuccess?.(data);
    },
    onError: (err) => options?.onError?.(err),
  });
}

export function useDeleteCarrierServiceMarkup(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierServiceId,
    }: {
      carrierServiceId: string;
    }): Promise<void> => {
      await axios.delete(
        `${API_URL}/api/v2/admin/platform-service-fees/${carrierServiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier-service-markup"],
      });
      options?.onSuccess?.();
    },
    onError: (err) => options?.onError?.(err),
  });
}

/** Format markup as a one-line display string. */
export function formatMarkup(
  m: {
    percent: number;
    flatCents: number;
  } | null
): string {
  if (!m) return "(none)";
  // Both parts, always. "10% + $1.50".
  const pct = `${m.percent}%`;
  const flat = `$${(m.flatCents / 100).toFixed(2)}`;
  return `${pct} + ${flat}`;
}
